import parse from 'html-react-parser'
import Image from 'next/image'
import Link from 'next/link'
import React, { type FC, type PropsWithChildren, useMemo, useRef } from 'react'
import { useTheme } from 'styled-components'

import {
  ButtonModifier,
  ButtonTypes,
  IconAligns,
} from '@components/atoms/Button/Button'
import Col from '@components/atoms/Grid/Col'
import Row from '@components/atoms/Grid/Row'
import Headline from '@components/atoms/Headline'
import { HeadlineImportances } from '@components/atoms/Headline/Headline'
import IconCoupons from '@components/atoms/Icons/IconCoupons'
import { Text, TextVariants } from '@components/atoms/Text/Text'
import AddressDetails from '@components/molecules/AddressDetails'
import Coupons from '@components/molecules/Coupons'
import { dummyCouponData } from '@components/molecules/Coupons/Coupons.data'
import OpeningHours from '@components/molecules/OpeningHours'
import ServiceList from '@components/molecules/ServiceList'
import { FilterSunpoint } from '@components/organisms/StudioFilter/StudioFilter'

import { parseOptions } from '@helper/parseOptions'
import { type PropsWithClassName } from '@helper/PropsWithClassName'
import { themeSunpoint } from '@helper/theme'

import { RoutesSunpoint } from '@definitions/routes/RoutesSunpoint'
import type {
  Category as CategoryType,
  Contract as ContractType,
  Coupon,
  Studio,
} from '@definitions/types/symfonyTypesd'

import {
  BrandedHeadline,
  Center,
  CenterCenterCol,
  Root,
  StudioDetailPageHead,
  StudioInfo,
  StyledButton,
  StyledContracts,
  StyledHeadText,
} from './StudioDetailPage.styles'

interface Props extends PropsWithClassName {
  studio: Studio
  handleSelectContract?: (contract: ContractType) => void
}

const StudioDetailPage: FC<PropsWithChildren<Props>> = (
  props: PropsWithChildren<Props>
): React.ReactElement => {
  const couponsRef = useRef<null | HTMLDivElement>(null)
  const { studio, handleSelectContract } = props
  const contracts = studio.contracts?.edges?.map(
    (contract) => contract?.node
  ) as ContractType[]
  const coupons =
    (studio.coupons?.edges?.map(
      (coupons) => coupons?.node
    ) as unknown as Coupon[]) || dummyCouponData

  const scrollToCoupons = () =>
    couponsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })

  const theme: any = useTheme()
  const orderedCategories = [...(studio.categories?.edges ?? [])].sort(
    (a, b) => (a?.node?.sort ?? 0) - (b?.node?.sort ?? 0)
  )

  const groupedServiceCategories: {
    parentName: string
    categories: CategoryType[]
  }[] = useMemo(() => {
    const categoryParents: { parentName: string; sort: number }[] = []
    const grouped: Map<string, CategoryType[]> = new Map<
      string,
      CategoryType[]
    >()
    for (let category of orderedCategories) {
      if (
        !category?.node?.service ||
        (category?.node?.affiliation?.name ?? '').toLowerCase() !== theme.key
      ) {
        continue
      }

      const parent = category?.node?.parent
      const parentName: string = parent?.name ?? 'Sonstiges'

      if (!grouped.has(parentName)) {
        grouped.set(parentName, [])
        categoryParents.push({
          parentName,
          sort: parent ? parent.sort ?? 0 : Infinity, // 'other' category should always be at the end
        })
      }

      grouped.get(parentName)?.push(category?.node)
    }

    categoryParents.sort((a, b) => a.sort - b.sort)
    return categoryParents.map(({ parentName }) => ({
      parentName,
      categories: grouped.get(parentName)!,
    }))
  }, [studio, theme.key])

  if (studio.title === undefined) {
    return (
      <Root className={props.className} data-testid={'studio-root'}>
        <Row>
          <Col xs={12} sm={12} md={12} lg={12}>
            <Headline importance={HeadlineImportances.h1}>No Studio</Headline>
          </Col>
        </Row>
      </Root>
    )
  }

  return (
    <Root className={props.className} data-testid={'studio-root'}>
      <StudioDetailPageHead>
        <Row>
          <Col xs={12} sm={12} md={12} lgOffset={1} lg={10}>
            <Headline importance={HeadlineImportances.h1}>
              {studio.title}
            </Headline>
            <StyledHeadText variant={TextVariants.lead}>
              SUNPOINT Beauty- & Tanning-Studio
            </StyledHeadText>
          </Col>
          <>
            {studio.additionalInfo && (
              <StudioInfo xs={12} lgOffset={2} lg={8}>
                {parse(studio.additionalInfo, parseOptions)}
              </StudioInfo>
            )}
          </>
        </Row>
      </StudioDetailPageHead>
      <Row>
        <Col xs={12} sm={6} lgOffset={1} lg={5}>
          <AddressDetails
            address={studio.address}
            phone={studio.phone}
            route={
              studio.latitude && studio.longitude
                ? `https://www.google.com/maps/search/?api=1&query=${studio.latitude},${studio.longitude}`
                : undefined
            }
            parkingAvailable={studio.parkingAvailable}
          />
          {coupons.length === 0 && (
            <p>
              Leider stehen für dieses Studio derzeit keine Coupons zur
              Verfügung
            </p>
          )}
        </Col>
        <Col xs={12} sm={6} lg={5}>
          <OpeningHours
            data={{
              weekdays: studio.openingHoursWeekdays,
              saturday: studio.openingHoursSaturday,
              sunday: studio.openingHoursSunday,
              publicHoliday: studio.openingHoursPublicHoliday,
              lastEntrance: studio.openingHoursLastEntrance,
              title: 'Öffnungszeiten Sunpoint',
              specialOpeningHour: studio.specialOpeningHour,
            }}
          />
        </Col>
      </Row>
      <Row>
        <>
          {coupons.length > 0 ? (
            <CenterCenterCol
              xs={12}
              sm={
                props.studio?.operator?.name === 'SUNPOINT AG & Co. KG' ? 6 : 12
              }
              md={
                props.studio?.operator?.name === 'SUNPOINT AG & Co. KG' ? 6 : 12
              }
              lg={
                props.studio?.operator?.name === 'SUNPOINT AG & Co. KG' ? 6 : 12
              }
            >
              <StyledButton
                buttonType={ButtonTypes.BUTTON}
                modifier={ButtonModifier.SECONDARY}
                disabled={false}
                loading={false}
                icon={<IconCoupons />}
                iconAlign={IconAligns.LEFT}
                onClick={scrollToCoupons}
              >
                COUPONS für dieses Studio
              </StyledButton>
            </CenterCenterCol>
          ) : null}
        </>
        <>
          {props.studio?.operator?.name === 'SUNPOINT AG & Co. KG' && (
            <CenterCenterCol
              xs={12}
              sm={coupons ? 6 : 12}
              md={coupons ? 6 : 12}
              lg={coupons ? 6 : 12}
            >
              <Link
                href={'https://www.sunpoint.de/sunpoint/sunpoint-clubsystem'}
              >
                <Image
                  src={
                    '/img/csm_SUNPOINT-Filialkette-Clubsystem_6a1912403f.jpg'
                  }
                  width={635}
                  height={400}
                  alt="Studiofinder Sunpoint"
                />
              </Link>
            </CenterCenterCol>
          )}
        </>
      </Row>
      <Row>
        <Col xs={12}>
          {contracts && handleSelectContract ? (
            <Center>
              {props.studio?.operator?.name === 'SUNPOINT AG & Co. KG' && (
                <Headline importance={HeadlineImportances.h2}>
                  IM CLUB SPARST DU MEHR
                </Headline>
              )}
              <StyledContracts
                handleSelectContract={handleSelectContract}
                contracts={contracts}
                studio={props.studio}
                showStudioButton={false}
              />
            </Center>
          ) : null}
        </Col>
        <Col xs={12} sm={12} md={12} lg={12}>
          <StudioDetailPageHead>
            {props.studio?.operator?.name === 'SUNPOINT AG & Co. KG' && (
              <StyledHeadText variant={TextVariants.paragraph}>
                Betreiber/in: {props.studio?.operator?.name},{' '}
                {props.studio?.operator?.street},{' '}
                {props.studio?.operator?.location}
                <br />
                Bei diesem Vertragspartner ist eine studioübergreifende
                Clubkartennutzung in über 100 Studios möglich.
              </StyledHeadText>
            )}
            {props.studio?.operator?.name !== 'SUNPOINT AG & Co. KG' && (
              <StyledHeadText variant={TextVariants.paragraph}>
                Betreiber/in: {props.studio?.operator?.name},{' '}
                {props.studio?.operator?.street},{' '}
                {props.studio?.operator?.location}
                <br />
                Bei diesem Vertragspartner ist eine studioübergreifende
                Clubkartennutzung nicht möglich.
              </StyledHeadText>
            )}
          </StudioDetailPageHead>
        </Col>
        <Col xs={12}>
          <Headline importance={HeadlineImportances.h2}>Leistungen</Headline>
        </Col>
        <>
          {groupedServiceCategories.map(({ parentName, categories }, index) => {
            let color: string = themeSunpoint.palette.default.primary
            if (parentName === FilterSunpoint.KOSMETIK) {
              color = themeSunpoint.palette.default.grey
            } else if (parentName === FilterSunpoint.BODYFORMING) {
              color = themeSunpoint.palette.default.secondary
            } else if (parentName === FilterSunpoint.BRAEUNE) {
              color = themeSunpoint.palette.default.primary
            }
            return (
              <Col xs={12} key={index}>
                <BrandedHeadline
                  importance={HeadlineImportances.h3}
                  color={color}
                >
                  {parentName}
                </BrandedHeadline>
                <ServiceList parentName={parentName} items={categories} />
              </Col>
            )
          })}
        </>
        <Col xs={12}>
          {coupons.length > 0 && (
            <div id="coupons" ref={couponsRef}>
              <Headline importance={HeadlineImportances.h2}>Coupons</Headline>
            </div>
          )}
          <>{coupons ? <Coupons coupons={coupons} studio={studio} /> : null}</>
        </Col>
        <>
          {studio.info && (
            <StudioInfo xs={12} lgOffset={2} lg={8}>
              {parse(studio.info, parseOptions)}
            </StudioInfo>
          )}
        </>
      </Row>
    </Root>
  )
}

export { StudioDetailPage }
